import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import {
  api,
  type ScheduledEmail,
  type ScheduleEmailResult,
  type EmailActionResult,
} from '../api/client.js';

// ─── Scheduled Emails ─────────────────────────────────────────────────────

export function useScheduledEmails(eventId: string) {
  return useQuery<{ scheduledEmails: ScheduledEmail[] }>({
    queryKey: ['scheduledEmails', eventId],
    queryFn: () => api.email.getScheduledEmails(eventId),
    enabled: !!eventId,
  });
}

export function useScheduleReminder() {
  const queryClient = useQueryClient();
  return useMutation<ScheduleEmailResult, Error, { eventId: string; daysBefore: number }>({
    mutationFn: ({ eventId, daysBefore }) =>
      api.email.scheduleReminder(eventId, daysBefore),
    onSuccess: (_result, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: ['scheduledEmails', eventId] });
    },
  });
}

export function useScheduleSend() {
  const queryClient = useQueryClient();
  return useMutation<
    ScheduleEmailResult,
    Error,
    { eventId: string; sendAt: string; emailType: string }
  >({
    mutationFn: ({ eventId, sendAt, emailType }) =>
      api.email.scheduleSend(eventId, sendAt, emailType),
    onSuccess: (_result, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: ['scheduledEmails', eventId] });
    },
  });
}

export function useCancelScheduledEmail() {
  const queryClient = useQueryClient();
  return useMutation<{ message: string }, Error, { scheduledId: string; eventId: string }>({
    mutationFn: ({ scheduledId }) => api.email.cancelScheduledEmail(scheduledId),
    onSuccess: (_result, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: ['scheduledEmails', eventId] });
    },
  });
}

// ─── Email Actions ────────────────────────────────────────────────────────

export function useSendCancellation() {
  const queryClient = useQueryClient();
  return useMutation<EmailActionResult, Error, string>({
    mutationFn: (eventId) => api.email.sendCancellation(eventId),
    onSuccess: (_result, eventId) => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', eventId] });
      queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useSendThankYou() {
  const queryClient = useQueryClient();
  return useMutation<EmailActionResult, Error, string>({
    mutationFn: (eventId) => api.email.sendThankYou(eventId),
    onSuccess: (_result, eventId) => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', eventId] });
      queryClient.invalidateQueries({ queryKey: ['event', eventId] });
      queryClient.invalidateQueries({ queryKey: ['events'] });
    },
  });
}

export function useSendUpdate() {
  const queryClient = useQueryClient();
  return useMutation<EmailActionResult, Error, { eventId: string; message: string }>({
    mutationFn: ({ eventId, message }) => api.email.sendUpdate(eventId, message),
    onSuccess: (_result, { eventId }) => {
      queryClient.invalidateQueries({ queryKey: ['dashboard', eventId] });
    },
  });
}
